import React, { Suspense, useMemo } from 'react';
import type { CSSProperties } from 'react';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import ReactMarkdown from 'react-markdown';
import remarkParse from 'remark-parse';
import remarkStringify from 'remark-stringify';
import unified from 'unified';
import styled from 'types/theme.types';
import { observer } from 'mobx-react';
import { useTheme } from 'hooks/use-theme';

const codeStyleOverrides: CSSProperties = {
  padding: '1em',
  borderRadius: '0.2em'
};

const LazyCodeBlock = React.lazy(async () => {
  const [highlighterMod, stylesMod] = await Promise.all([
    import('react-syntax-highlighter/dist/cjs/prism'),
    import('react-syntax-highlighter/dist/cjs/styles/prism')
  ]);
  const SyntaxHighlighter = highlighterMod.default;
  const { coy, darcula } = stylesMod;

  const CodeBlock = ({
    isDarkTheme,
    customStyle,
    language,
    children,
    dataE2e,
    ...props
  }: {
    isDarkTheme: boolean;
    customStyle: CSSProperties;
    language: string;
    children: string;
    dataE2e?: string;
  }) => (
    <SyntaxHighlighter
      style={isDarkTheme ? darcula : coy}
      language={language}
      PreTag="div"
      data-e2e={dataE2e}
      customStyle={customStyle}
      {...props}>
      {children}
    </SyntaxHighlighter>
  );

  return { default: CodeBlock };
});

const Markdown: React.FunctionComponent<{
  markdown: string;
  className?: string;
  dataE2e?: string;
}> = ({ markdown, className, dataE2e }) => {
  const { appTheme, isDarkTheme } = useTheme();

  const customStyle = useMemo(
    () => ({
      ...codeStyleOverrides,
      border: `1px solid ${appTheme.lightGray}`,
      backgroundColor: appTheme.markdownBackground
    }),
    [appTheme]
  );

  const normalizedMarkdown = useMemo(() => normalizeLineBreaks(markdown), [markdown]);

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      linkTarget="_blank"
      className={className}
      components={{
        table: ({ node, ...props }) => <StyledTable {...props} />,
        code({ node, inline, className: rawClassName, children, ref, ...props }) {
          const codeClassName = rawClassName as string;
          const match = /language-(\w+)/.exec(codeClassName || '');
          return !inline && match ? (
            <Suspense
              fallback={
                <pre>
                  <code>{String(children)}</code>
                </pre>
              }>
              <LazyCodeBlock
                isDarkTheme={isDarkTheme}
                customStyle={customStyle}
                language={match[1]}
                dataE2e={dataE2e}
                {...props}>
                {String(children).replace(/\n$/, '')}
              </LazyCodeBlock>
            </Suspense>
          ) : (
            <code className={codeClassName} {...props}>
              {children}
            </code>
          );
        }
      }}>
      {normalizedMarkdown}
    </ReactMarkdown>
  );
};

export default observer(Markdown);

function normalizeLineBreaks(markdown: string) {
  const tree = unified().use(remarkParse).parse(markdown);
  return unified().use(remarkStringify).stringify(tree);
}

const StyledTable = styled.table`
  width: max-content;
  max-width: 100%;
  overflow: auto;
  border-collapse: collapse;
  margin-bottom: 10px;

  tr:nth-child(2n) {
    background-color: ${({ theme }) => theme.plainGray};
  }

  th {
    text-align: center !important;
  }

  th,
  td {
    padding: 6px 13px;
    border: 1px solid ${({ theme }) => theme.lightGray};
  }
`;
